import React, { useMemo, useState } from 'react';
import { cn } from '../../../utils/utils';
import CardCom from '../../../components/CardCom/index.jsx';
import ReactECharts from 'echarts-for-react'; 
import 'echarts-wordcloud';

import StarSvg from '../../../assets/svgs/star.svg';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { Divider } from 'antd';

const OverviewCom = ({detailInfo}) => {
  const [tabIndex, setTabIndex] = useState(1);

  const wordCloudOptions = useMemo(() => {
    const data = detailInfo.wordcloud ? detailInfo.wordcloud.map(item => {
      return {
        name: item.word,
        value: item.count
      };
    }) : [];
    return {
      tooltip: {},
      series: [ {
        type: 'wordCloud',
        gridSize: 2,
        sizeRange: [12, 50],
        rotationRange: [-90, 90],
        shape: 'pentagon',
        width: 600,
        height: 400,
        drawOutOfBound: true,
        textStyle: {
          color: function () {
            return 'rgb(' + [
              Math.round(Math.random() * 160),
              Math.round(Math.random() * 160),
              Math.round(Math.random() * 160)
            ].join(',') + ')';
          }
        },
        emphasis: {
          textStyle: {
            shadowBlur: 10,
            shadowColor: '#333'
          }
        },
        data: data
      }]
    };
  }, []);

  const sentimentPieOptions = useMemo(() => {
    const { sentiment } = detailInfo;
    const { counts } = sentiment;
    const { negative, neutral, positive } = counts;
    return {
      tooltip: {
        trigger: 'item',
        formatter: '{a} <br/> {b}: {c} ({d}%)'
      },
      series: [
        {
          name: 'Sentence sentiment',
          type: 'pie',
          radius: ['40%', '70%'],
          avoidLabelOverlap: false,
          label: {
            show: true,
            color: '#8FBF71',
            fontSize: 15,
            // padding: [0, -60, 0, -100],
          },
          labelLine: {
            show: true,
            length: 20,
            length2: 30,
            lineStyle: {
              color: '#000000',
              width: 2
            }
          },
          data: [
            { value: positive, name: 'Positive', itemStyle: {
              color:'#22AE4A'
            }},
            { value: negative, name: 'Negative', itemStyle: {
              color: '#FF8C8C'
            }},
            { value: neutral, name: 'Neutral', itemStyle: {
              color: '#FFCE1F'
            }},
          ]
        },
        {
          tooltip: {
            show: false
          },
          type: 'pie',
          radius: ['35%', '40%'],
          label: {
            show: false
          },
          emphasis: {
            label: {
              show: false
            }
          },
          labelLine: {
            show: false
          },
          data: [
            { value: positive, name: 'Positive', itemStyle: {
              color:'#34783C'
            }},
            { value: negative, name: 'Negative', itemStyle: {
              color: '#D77575'
            }},
            { value: neutral, name: 'Neutral', itemStyle: {
              color: '#FFB11F'
            }},
          ]
        }
      ]
    };
  }, []);

  const topicPieOptions = useMemo(() => {
    let { topics = [] } = detailInfo;
    topics = topics || [];
    const data = topics.map(item => {
      return { value: item.count, name: item.topic };
    });
    return {
      legend: {
        orient: 'vertical',
        left: '50%',
        top: 'center',
        align: 'left',
        textStyle: {
          color: '#00A32E',
          fontSize: 16,
          fontWeight: 400
        }
      },
      series: [
        {
          name: 'Nightingale Chart',
          type: 'pie',
          roseType: 'radius',
          radius: ['2%', '90%'],
          center: ['25%', '50%'],
          avoidLabelOverlap: true,
          padAngle: 5,
          itemStyle: {
            borderRadius: 5,
          },
          label: {
            show: true,
            position: 'inside',
            formatter: '{d}%',
            color: '#ffffff',
            fontSize: '18',
            fontWeight: 600
          },
          data: data
        }
      ]
    };
  }, []);

  const themeData = useMemo(() => {
    const { themes } = detailInfo;
    let curPage = 1;
    const pageSize = 4;
    const totalPages = Math.ceil(themes.length / pageSize);
    if(tabIndex <= 0) {
      curPage = 1;
    } else if(tabIndex > totalPages) {
      curPage = totalPages;
    } else {
      curPage = tabIndex;
    }
    const startIndex = (curPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
  
    return themes.slice(startIndex, endIndex);
  }, [tabIndex, detailInfo]);

  const countPercent = (left = 0, right = 0) => {
    right = +right;
    left = +left;
    if(right === 0) return 0;
    return left / (left + right) * 100;
  };
  return (
    <div className={cn('grid gap-4')}>
      <CardCom label="Auto-video summarization">
        <div className="flex justify-start">
          {/* <video className="h-[220px]" autoPlay={false} muted loop controls src="https://ec-crm-aliyun-oss.bluemoon.com.cn/officialwebsite/website/pro/website_20247_1724325255689.mp4" preload="true"></video> */}
          <div className="flex-1">
            <div className="border rounded-[14px] border-primary p-[10px] flex">
              <img src={StarSvg} className='mt-[3px] w-[16px] h-[16px]' alt="" />
              <div className="text-sm text-primary flex-1 ml-2">
                <b>Hey! Did you know</b> Unique themes identified from your responses are displayed below. Clicking any theme will refine the video summary to the left.
              </div>
            </div>
            <div className="mt-6 mb-[20px] flex justify-between items-center">
              <span className="text-lg leading-none font-semibold text-primary">Unique themes based on your video responses</span>
              <div className="flex items-center text-primary text-lg">
                <LeftOutlined className=" cursor-pointer" onClick={() => setTabIndex(tabIndex - 1)} />
                <div className="w-5"></div>
                <RightOutlined className=" cursor-pointer" onClick={() => setTabIndex(tabIndex + 1)} />
              </div>
            </div>

            <div className="grid gap-4 grid-cols-2">
              {
                themeData.map((item, index) => <div key={index} className="bg-white rounded-xl p-3">
                  <span className="text-base leading-none text-primary font-semibold">{item.theme}</span>
                  <Divider className="bg-[#EAF4EA] my-3" />
                  <div className="flex justify-between items-center text-sm font-semibold leading-none mb-3">
                    <div className="text-[#34783C99]">Responses <span className="text-primary">{item.response_ids.length}</span></div>
                    <div className="text-[#34783C99]">Mentions <span className="text-primary">{item.mentions}</span></div>
                  </div>
                  <div className="h-4 w-full rounded-full overflow-hidden flex items-center">
                    <div className="bg-red-300 h-full" style={{width: `${countPercent(item.response_ids.length, item.mentions)}%`}}></div>
                    <div className="bg-green-600 h-full" style={{width: `${countPercent(item.mentions, item.response_ids.length)}%`}}></div>
                  </div>
                </div>)
              }
            </div>
          </div>
        </div>
      </CardCom>
      <div className="grid gap-4 grid-cols-2">
        <CardCom label="Word cloud">
          <ReactECharts 
            option={wordCloudOptions}
          ></ReactECharts>
        </CardCom>

        <CardCom label="Sentence sentiment">
          <ReactECharts
            option={sentimentPieOptions}
          />
        </CardCom>
      </div>

      <CardCom label="Auto-video summarization">
        <ReactECharts 
          option={topicPieOptions}
          style={{height: '400px'}}
        />
      </CardCom>
    </div>
  );
};

export default OverviewCom;